import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  LayoutDashboard, Mail, Bell, Users, Download, LogOut,
  Send, X, Loader2, MessageCircle, ChevronDown,
  FileText, CalendarDays, ShoppingBag, Briefcase, GraduationCap, Car,
  Plus, Pencil, Trash2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'contacts', label: 'Contacts', icon: Mail },
  { id: 'alerts', label: 'Car Alerts', icon: Bell },
  { id: 'subscribers', label: 'Subscribers', icon: Users },
  { id: 'vehicles', label: 'Vehicles', icon: Car },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'events', label: 'Events', icon: CalendarDays },
  { id: 'shop', label: 'Shop', icon: ShoppingBag },
  { id: 'careers', label: 'Careers', icon: Briefcase },
  { id: 'academy', label: 'Academy', icon: GraduationCap },
];

const STATUS_OPTIONS = ['active', 'contacted', 'fulfilled', 'closed'];
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  contacted: 'bg-blue-100 text-blue-800',
  fulfilled: 'bg-purple-100 text-purple-800',
  closed: 'bg-gray-100 text-gray-600',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ── Generic CRUD Modal ────────────────────────────── */
const CrudModal = ({ title, fields, item, onSave, onClose, saving }) => {
  const [form, setForm] = useState(() => {
    const initial = {};
    fields.forEach(f => { initial[f.key] = item ? (item[f.key] ?? f.default ?? '') : (f.default ?? ''); });
    return initial;
  });

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="crud-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
          <h3 className="font-semibold text-lg">{item ? `Edit ${title}` : `Add ${title}`}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-sm text-gray-400 mb-1">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)} rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid={`crud-field-${f.key}`} />
              ) : f.type === 'select' ? (
                <select value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`crud-field-${f.key}`}>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'checkbox' ? (
                <input type="checkbox" checked={!!form[f.key]} onChange={e => handleChange(f.key, e.target.checked)}
                  className="w-5 h-5" data-testid={`crud-field-${f.key}`} />
              ) : f.type === 'number' ? (
                <input type="number" value={form[f.key]} onChange={e => handleChange(f.key, parseInt(e.target.value) || '')}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`crud-field-${f.key}`} />
              ) : (
                <input type="text" value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid={`crud-field-${f.key}`} />
              )}
            </div>
          ))}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="border-gray-700 text-gray-300">Cancel</Button>
            <Button onClick={() => onSave(form)} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="crud-save">
              {saving && <Loader2 className="animate-spin mr-2" size={14} />}
              {item ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Generic Content Tab ───────────────────────────── */
const ContentTab = ({ title, endpoint, columns, fields, token, items, setItems }) => {
  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editItem) {
        const res = await fetch(`${API_URL}/api/admin/${endpoint}/${editItem.id}`, { method: 'PUT', headers, body: JSON.stringify(form) });
        if (res.ok) {
          const updated = await res.json();
          setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        }
      } else {
        const res = await fetch(`${API_URL}/api/admin/${endpoint}`, { method: 'POST', headers, body: JSON.stringify(form) });
        if (res.ok) {
          const created = await res.json();
          setItems(prev => [created, ...prev]);
        }
      }
      setShowModal(false);
      setEditItem(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return;
    const res = await fetch(`${API_URL}/api/admin/${endpoint}/${id}`, { method: 'DELETE', headers });
    if (res.ok) setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{title}</h1>
        <Button onClick={() => { setEditItem(null); setShowModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid={`add-${endpoint}`}>
          <Plus size={14} className="mr-2" /> Add {title.replace(/s$/, '')}
        </Button>
      </div>
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              {columns.map(c => <th key={c.key} className="p-3">{c.label}</th>)}
              <th className="p-3 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} className="border-b border-gray-800/50 hover:bg-white/5">
                {columns.map(c => (
                  <td key={c.key} className="p-3 text-gray-300">
                    {c.render ? c.render(item) : (
                      c.key === 'image' || c.key === 'main_image' ? (
                        item[c.key] ? <img src={item[c.key]} alt="" className="w-12 h-8 object-cover rounded" /> : '—'
                      ) : typeof item[c.key] === 'boolean' ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item[c.key] ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-500'}`}>
                          {item[c.key] ? 'Yes' : 'No'}
                        </span>
                      ) : (String(item[c.key] || '—').substring(0, 60))
                    )}
                  </td>
                ))}
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setEditItem(item); setShowModal(true); }} className="text-blue-400 hover:text-blue-300" data-testid={`edit-${item.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-${item.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-center text-gray-500 py-8">No items yet</p>}
      </div>
      {showModal && (
        <CrudModal
          title={title.replace(/s$/, '')}
          fields={fields}
          item={editItem}
          saving={saving}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditItem(null); }}
        />
      )}
    </div>
  );
};

/* ── Main Dashboard ────────────────────────────────── */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyModal, setReplyModal] = useState(null);
  const [replyMsg, setReplyMsg] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const token = localStorage.getItem('admin_token');
  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/admin/login'); return; }
    setLoading(true);
    const h = { 'Authorization': `Bearer ${token}` };
    try {
      const [s, c, a, sub, v, bp, ev, pr, ca, co] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: h }).then(r => r.ok ? r.json() : Promise.reject()),
        fetch(`${API_URL}/api/admin/contacts`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/car-alerts`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/subscribers`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/vehicles`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/blog`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/events`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/shop`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/careers`, { headers: h }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/academy`, { headers: h }).then(r => r.ok ? r.json() : []),
      ]);
      setStats(s); setContacts(c); setAlerts(a); setSubscribers(sub);
      setVehicles(v); setBlogPosts(bp); setEvents(ev); setProducts(pr); setCareers(ca); setCourses(co);
    } catch {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const logout = () => { localStorage.removeItem('admin_token'); navigate('/admin/login'); };

  const exportCSV = async (type) => {
    const res = await fetch(`${API_URL}/api/admin/export/${type}`, { headers: { 'Authorization': `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${type}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const updateAlertStatus = async (id, status) => {
    await fetch(`${API_URL}/api/admin/car-alerts/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }) });
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, status } : a));
  };

  const sendReply = async () => {
    if (!replyModal || !replyMsg.trim()) return;
    setSending(true);
    await fetch(`${API_URL}/api/admin/reply`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ to_email: replyModal.email, to_name: replyModal.name, subject: replySubject, message: replyMsg })
    });
    setSending(false); setReplyModal(null); setReplyMsg(''); setReplySubject('');
  };

  const openReply = (name, email, defaultSubject) => {
    setReplyModal({ name, email }); setReplySubject(defaultSubject || 'Re: Your inquiry — Revs Vibes'); setReplyMsg('');
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-950 flex items-center justify-center"><Loader2 className="animate-spin text-white" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-56 bg-gray-900 border-r border-gray-800 flex flex-col z-40 overflow-y-auto">
        <div className="p-5 border-b border-gray-800">
          <h2 className="font-bold text-lg tracking-wide">REVS VIBES</h2>
          <p className="text-xs text-gray-500 mt-0.5">Admin Dashboard</p>
        </div>
        <nav className="flex-1 py-4">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors ${tab === t.id ? 'bg-white/10 text-white font-medium' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
              <t.icon size={18} />{t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-800">
          <button onClick={logout} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="admin-logout">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-56 p-8 min-h-screen">
        {/* Overview */}
        {tab === 'overview' && stats && (
          <div>
            <h1 className="text-2xl font-bold mb-6" data-testid="admin-overview-title">Dashboard</h1>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { label: 'Contacts', value: stats.contacts, color: 'border-blue-500' },
                { label: 'Car Alerts', value: stats.car_alerts, color: 'border-green-500' },
                { label: 'Active Alerts', value: stats.active_alerts, color: 'border-yellow-500' },
                { label: 'Subscribers', value: stats.subscribers, color: 'border-purple-500' },
                { label: 'Vehicles', value: stats.vehicles, color: 'border-red-500' },
                { label: 'Blog Posts', value: stats.blog_posts, color: 'border-cyan-500' },
                { label: 'Events', value: stats.events, color: 'border-orange-500' },
                { label: 'Products', value: stats.products, color: 'border-pink-500' },
                { label: 'Careers', value: stats.careers, color: 'border-emerald-500' },
                { label: 'Courses', value: stats.courses, color: 'border-indigo-500' },
              ].map(s => (
                <Card key={s.label} className={`bg-gray-900 border-l-4 ${s.color} border-t-0 border-r-0 border-b-0`}>
                  <CardContent className="p-4">
                    <p className="text-gray-400 text-xs">{s.label}</p>
                    <p className="text-2xl font-bold mt-1" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <h2 className="text-lg font-semibold mb-4">Recent Inquiries</h2>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-400 text-left">
                  <th className="p-3">Name</th><th className="p-3">Subject</th><th className="p-3">Date</th><th className="p-3">Action</th>
                </tr></thead>
                <tbody>
                  {contacts.slice(0, 5).map(c => (
                    <tr key={c.id} className="border-b border-gray-800/50 hover:bg-white/5">
                      <td className="p-3">{c.name}<br/><span className="text-gray-500 text-xs">{c.email}</span></td>
                      <td className="p-3 text-gray-300">{c.subject}</td>
                      <td className="p-3 text-gray-500">{fmtDate(c.created_at)}</td>
                      <td className="p-3">
                        <button onClick={() => openReply(c.name, c.email, `Re: ${c.subject}`)} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                          <Send size={12} /> Reply
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Contacts tab */}
        {tab === 'contacts' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">Contact Inquiries</h1>
              <Button onClick={() => exportCSV('contacts')} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:text-white" data-testid="export-contacts">
                <Download size={14} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-400 text-left">
                  <th className="p-3">Name</th><th className="p-3">Subject</th><th className="p-3">Phone</th><th className="p-3">Date</th><th className="p-3">Actions</th>
                </tr></thead>
                <tbody>
                  {contacts.map(c => (
                    <React.Fragment key={c.id}>
                      <tr className="border-b border-gray-800/50 hover:bg-white/5 cursor-pointer" onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}>
                        <td className="p-3">{c.name}<br/><span className="text-gray-500 text-xs">{c.email}</span></td>
                        <td className="p-3 text-gray-300">{c.subject}</td>
                        <td className="p-3 text-gray-400">{c.phone || '—'}</td>
                        <td className="p-3 text-gray-500">{fmtDate(c.created_at)}</td>
                        <td className="p-3 flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); openReply(c.name, c.email, `Re: ${c.subject}`); }} className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                            <Send size={12} /> Reply
                          </button>
                          <ChevronDown size={14} className={`text-gray-500 transition-transform ${expandedRow === c.id ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>
                      {expandedRow === c.id && (
                        <tr><td colSpan={5} className="p-4 bg-gray-800/50 text-gray-300 text-sm whitespace-pre-wrap">{c.message}</td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {contacts.length === 0 && <p className="text-center text-gray-500 py-8">No inquiries yet</p>}
            </div>
          </div>
        )}

        {/* Car Alerts tab */}
        {tab === 'alerts' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">Car Alerts</h1>
              <Button onClick={() => exportCSV('car-alerts')} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:text-white" data-testid="export-alerts">
                <Download size={14} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-400 text-left">
                  <th className="p-3">Client</th><th className="p-3">Vehicle</th><th className="p-3">Year</th><th className="p-3">Budget</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3">Actions</th>
                </tr></thead>
                <tbody>
                  {alerts.map(a => (
                    <React.Fragment key={a.id}>
                      <tr className="border-b border-gray-800/50 hover:bg-white/5 cursor-pointer" onClick={() => setExpandedRow(expandedRow === a.id ? null : a.id)}>
                        <td className="p-3">{a.name}<br/><span className="text-gray-500 text-xs">{a.email}</span></td>
                        <td className="p-3 font-medium">{a.make} {a.model}</td>
                        <td className="p-3 text-gray-400">{a.year_from || '—'} – {a.year_to || '—'}</td>
                        <td className="p-3 text-gray-400">
                          {a.budget_min || a.budget_max ? `$${(a.budget_min || 0).toLocaleString()} – $${(a.budget_max || 0).toLocaleString()}` : '—'}
                        </td>
                        <td className="p-3">
                          <select value={a.status} onChange={(e) => { e.stopPropagation(); updateAlertStatus(a.id, e.target.value); }}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-1 rounded-full font-medium cursor-pointer ${STATUS_COLORS[a.status] || STATUS_COLORS.active}`}
                            data-testid={`alert-status-${a.id}`}>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                          </select>
                        </td>
                        <td className="p-3 text-gray-500">{fmtDate(a.created_at)}</td>
                        <td className="p-3">
                          <button onClick={(e) => { e.stopPropagation(); openReply(a.name, a.email, `Re: Your ${a.make} ${a.model} Alert`); }}
                            className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                            <Send size={12} /> Reply
                          </button>
                        </td>
                      </tr>
                      {expandedRow === a.id && a.notes && (
                        <tr><td colSpan={7} className="p-4 bg-gray-800/50 text-gray-300 text-sm"><strong>Notes:</strong> {a.notes}</td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {alerts.length === 0 && <p className="text-center text-gray-500 py-8">No car alerts yet</p>}
            </div>
          </div>
        )}

        {/* Subscribers tab */}
        {tab === 'subscribers' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold">Newsletter Subscribers</h1>
              <Button onClick={() => exportCSV('subscribers')} variant="outline" size="sm" className="border-gray-700 text-gray-300 hover:text-white" data-testid="export-subscribers">
                <Download size={14} className="mr-2" /> Export CSV
              </Button>
            </div>
            <div className="bg-gray-900 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-800 text-gray-400 text-left">
                  <th className="p-3">Email</th><th className="p-3">Subscribed</th>
                </tr></thead>
                <tbody>
                  {subscribers.map(s => (
                    <tr key={s.id} className="border-b border-gray-800/50 hover:bg-white/5">
                      <td className="p-3">{s.email}</td>
                      <td className="p-3 text-gray-500">{fmtDate(s.subscribed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscribers.length === 0 && <p className="text-center text-gray-500 py-8">No subscribers yet</p>}
            </div>
          </div>
        )}

        {/* Vehicles CRUD */}
        {tab === 'vehicles' && (
          <ContentTab title="Vehicles" endpoint="vehicles" token={token} items={vehicles} setItems={setVehicles}
            columns={[
              { key: 'main_image', label: 'Image' },
              { key: 'year', label: 'Year' },
              { key: 'make', label: 'Make' },
              { key: 'model', label: 'Model' },
              { key: 'price', label: 'Price' },
              { key: 'featured', label: 'Featured' },
              { key: 'status', label: 'Status' },
            ]}
            fields={[
              { key: 'year', label: 'Year', type: 'number', default: 2024 },
              { key: 'make', label: 'Make' },
              { key: 'model', label: 'Model' },
              { key: 'mileage', label: 'Mileage' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'main_image', label: 'Image URL' },
              { key: 'price', label: 'Price', default: 'POA' },
              { key: 'featured', label: 'Featured', type: 'checkbox', default: false },
              { key: 'status', label: 'Status', type: 'select', options: ['available', 'sold', 'reserved'], default: 'available' },
            ]}
          />
        )}

        {/* Blog CRUD */}
        {tab === 'blog' && (
          <ContentTab title="Blog Posts" endpoint="blog" token={token} items={blogPosts} setItems={setBlogPosts}
            columns={[
              { key: 'image', label: 'Image' },
              { key: 'title', label: 'Title' },
              { key: 'category', label: 'Category' },
              { key: 'date', label: 'Date' },
              { key: 'published', label: 'Published' },
            ]}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'category', label: 'Category', type: 'select', options: ['Vehicle Spotlights', 'Market Trends', "Buyer's Guide", 'Events', 'Lifestyle'], default: 'Vehicle Spotlights' },
              { key: 'excerpt', label: 'Excerpt', type: 'textarea' },
              { key: 'content', label: 'Full Content', type: 'textarea' },
              { key: 'image', label: 'Image URL' },
              { key: 'read_time', label: 'Read Time', default: '5 min read' },
              { key: 'author', label: 'Author', default: 'Revs Vibes' },
              { key: 'published', label: 'Published', type: 'checkbox', default: true },
            ]}
          />
        )}

        {/* Events CRUD */}
        {tab === 'events' && (
          <ContentTab title="Events" endpoint="events" token={token} items={events} setItems={setEvents}
            columns={[
              { key: 'image', label: 'Image' },
              { key: 'title', label: 'Title' },
              { key: 'date', label: 'Date' },
              { key: 'location', label: 'Location' },
              { key: 'published', label: 'Published' },
            ]}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'date', label: 'Date' },
              { key: 'location', label: 'Location' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'image', label: 'Image URL' },
              { key: 'attendees', label: 'Expected Attendees' },
              { key: 'published', label: 'Published', type: 'checkbox', default: true },
            ]}
          />
        )}

        {/* Shop CRUD */}
        {tab === 'shop' && (
          <ContentTab title="Products" endpoint="shop" token={token} items={products} setItems={setProducts}
            columns={[
              { key: 'image', label: 'Image' },
              { key: 'name', label: 'Name' },
              { key: 'price', label: 'Price' },
              { key: 'category', label: 'Category' },
              { key: 'in_stock', label: 'In Stock' },
            ]}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'price', label: 'Price' },
              { key: 'category', label: 'Category', type: 'select', options: ['Clothing', 'Accessories', 'Art', 'Collectibles'], default: 'Clothing' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'image', label: 'Image URL' },
              { key: 'in_stock', label: 'In Stock', type: 'checkbox', default: true },
            ]}
          />
        )}

        {/* Careers CRUD */}
        {tab === 'careers' && (
          <ContentTab title="Careers" endpoint="careers" token={token} items={careers} setItems={setCareers}
            columns={[
              { key: 'title', label: 'Title' },
              { key: 'location', label: 'Location' },
              { key: 'type', label: 'Type' },
              { key: 'published', label: 'Published' },
            ]}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'location', label: 'Location', default: 'Orlando, FL' },
              { key: 'type', label: 'Type', type: 'select', options: ['Full-Time', 'Part-Time', 'Contract', 'Remote'], default: 'Full-Time' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'published', label: 'Published', type: 'checkbox', default: true },
            ]}
          />
        )}

        {/* Academy CRUD */}
        {tab === 'academy' && (
          <ContentTab title="Courses" endpoint="academy" token={token} items={courses} setItems={setCourses}
            columns={[
              { key: 'title', label: 'Title' },
              { key: 'duration', label: 'Duration' },
              { key: 'level', label: 'Level' },
              { key: 'published', label: 'Published' },
            ]}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'duration', label: 'Duration', default: '4 weeks' },
              { key: 'level', label: 'Level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced'], default: 'Beginner' },
              { key: 'description', label: 'Description', type: 'textarea' },
              { key: 'icon', label: 'Icon', type: 'select', options: ['BookOpen', 'Award', 'Video'], default: 'BookOpen' },
              { key: 'published', label: 'Published', type: 'checkbox', default: true },
            ]}
          />
        )}
      </main>

      {/* Reply Modal */}
      {replyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg" data-testid="reply-modal">
            <div className="flex items-center justify-between p-5 border-b border-gray-800">
              <div>
                <h3 className="font-semibold text-lg flex items-center gap-2"><MessageCircle size={18} /> Quick Reply</h3>
                <p className="text-sm text-gray-400 mt-0.5">To: {replyModal.name} ({replyModal.email})</p>
              </div>
              <button onClick={() => setReplyModal(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Subject</label>
                <input value={replySubject} onChange={(e) => setReplySubject(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="reply-subject" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Message</label>
                <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={5}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Type your reply..." data-testid="reply-message" />
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setReplyModal(null)} className="border-gray-700 text-gray-300">Cancel</Button>
                <Button onClick={sendReply} disabled={sending || !replyMsg.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="reply-send">
                  {sending ? <Loader2 className="animate-spin mr-2" size={14} /> : <Send size={14} className="mr-2" />}
                  Send Reply
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
